// src/components/IntroScreen.tsx — sovelluksen ensikäynnistyksen esittely
//
// Kaupasta asennettu sovellus ohittaa verkon etusivun (src/LandingPage.tsx),
// koska asentaja on jo päättänyt kokeilla. Tämä näytetään sen sijaan kerran:
// ensimmäisellä käynnistyksellä ennen kirjautumista. "Aloita" merkitsee sen
// nähdyksi, eikä sitä näytetä uudelleen. Sisältö tulee etusivulta, jotta
// lupaus ja kortit pysyvät samoina molemmissa.
//
// Esikatselu selaimessa: http://localhost:3000/?intro=1

import React, { useState } from "react";
import { LANDING_TEXT, LANDING_COLORS as K, FEATURE_ART } from "../LandingPage";

export const INTRO_SEEN_KEY = "spinloop_intro_seen";

const F = { heading: "'Rajdhani', sans-serif", body: "'Sora', sans-serif" };

export default function IntroScreen({ onDone }: { onDone: () => void }) {
  const [lang, setLang] = useState<"fi" | "en">(() => {
    try { return (localStorage.getItem("spinloop_lang") as "fi" | "en") || "fi"; } catch { return "fi"; }
  });
  const t = LANDING_TEXT[lang];

  function done() {
    try { localStorage.setItem(INTRO_SEEN_KEY, "1"); } catch { /* ei tallennusta, näytetään ensi kerralla uudelleen */ }
    onDone();
  }
  function toggleLang() {
    const next = lang === "fi" ? "en" : "fi";
    setLang(next);
    try { localStorage.setItem("spinloop_lang", next); } catch { /* ohitetaan */ }
  }

  const tones = [K.court, K.kitchen, K.ball];

  return (
    <div style={{
      minHeight: "100vh", fontFamily: F.body, color: "#fff",
      background: "#070b12",
      backgroundImage: [
        "radial-gradient(60% 45% at 85% 8%, rgba(29,111,184,0.42) 0%, rgba(29,111,184,0) 70%)",
        "radial-gradient(70% 40% at 50% 100%, rgba(47,143,107,0.25) 0%, rgba(47,143,107,0) 70%)",
        "linear-gradient(180deg, rgba(7,11,18,0.85) 0%, rgba(9,18,34,0.82) 50%, rgba(7,11,18,0.94) 100%)",
        "url('/court-bg.jpg')",
      ].join(", "),
      backgroundSize: "auto, auto, auto, cover", backgroundPosition: "center",
      padding: "calc(20px + env(safe-area-inset-top, 0px)) 20px calc(24px + env(safe-area-inset-bottom, 0px))",
      display: "flex", flexDirection: "column", alignItems: "center",
    }}>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .sl-intro-in { animation: sl-intro 0.5s ease-out both; }
          .sl-intro-in:nth-child(2) { animation-delay: 0.08s; }
          .sl-intro-in:nth-child(3) { animation-delay: 0.16s; }
          @keyframes sl-intro { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: 420, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={toggleLang} style={{
          background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.22)",
          borderRadius: 999, padding: "6px 14px", color: "#fff", fontSize: 12, fontWeight: 700,
          cursor: "pointer", fontFamily: F.body,
        }}>{lang === "fi" ? "EN" : "FI"}</button>
      </div>

      <div style={{ width: "100%", maxWidth: 420, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 22, paddingTop: 8 }}>
        <div style={{ textAlign: "center" }}>
          <img src="/logo512.png" alt="Spinloop" width={76} height={76}
            style={{ borderRadius: 18, boxShadow: "0 14px 32px rgba(0,0,0,0.55)", display: "block", margin: "0 auto 14px" }} />
          <h1 style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 34, lineHeight: 1.02, margin: "0 0 10px" }}>
            {t.h1a}<br />
            <span style={{ color: K.neon, textShadow: "0 0 22px rgba(217,255,58,0.45)" }}>{t.h1b}</span>
          </h1>
          <p style={{ fontSize: 14, lineHeight: 1.55, color: "rgba(255,255,255,0.75)", margin: 0 }}>{t.sub}</p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {t.features.map((f, i) => (
            <div key={f.title} className="sl-intro-in" style={{
              display: "flex", gap: 12, alignItems: "center",
              background: "linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0.03))",
              border: `1.5px solid ${tones[i]}`, borderRadius: 18, padding: 10,
              boxShadow: `0 0 26px ${tones[i]}2e`,
            }}>
              <img src={FEATURE_ART[i]} alt="" style={{ width: 64, height: 64, borderRadius: 14, flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: F.heading, fontWeight: 700, fontSize: 18, lineHeight: 1.1, marginBottom: 2 }}>{f.title}</div>
                <div style={{ fontSize: 12, lineHeight: 1.45, color: "rgba(255,255,255,0.7)" }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ width: "100%", maxWidth: 420, paddingTop: 18 }}>
        <button onClick={done} style={{
          width: "100%", background: K.neon, color: K.neonInk, border: "none", borderRadius: 14,
          padding: "16px 0", fontSize: 17, fontWeight: 700, fontFamily: F.heading, letterSpacing: 0.5,
          cursor: "pointer", boxShadow: "0 0 0 1px rgba(217,255,58,0.5), 0 12px 30px rgba(217,255,58,0.3)",
        }}>
          {lang === "fi" ? "Aloita" : "Get started"} →
        </button>
        <div style={{ textAlign: "center", fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 10 }}>
          {t.trust.join(" · ")}
        </div>
      </div>
    </div>
  );
}
